import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import { lineConsole } from "../../utils/lineConsoleClient";
import "../../styles/scss/lineAnalytics.scss";

/**
 * LINE配信の分析（D34）。
 *
 * 数字の定義は slash_line 側（apps/analytics/console_report.py）に集約してあり、
 * この画面は**受け取った値を描くだけ**。ファネルタブは build_overview（友だち数・
 * 現会員の連携率・体験/入会の LINE と WEB）を描く。
 */

// MUI の Select ドロップダウンは portal（body直下）に描画されるため、ページ側の
// スコープ付きCSSが当たらない。既定のライト配色のまま出て文字が読めなくなるので、
// 専用クラスを渡してグローバル側でダークに上書きする（セグメント配信ページと同じ手）。
const MENU_PROPS = { className: "line-analytics-menu" };

const TABS = [
  { key: "funnel", label: "ファネル" },
  { key: "link", label: "LINE連携" },
  { key: "scenarios", label: "シナリオ別" },
  { key: "users", label: "人の動き" },
];

// slash_line を公式LINEにつないだ日。「全期間」の起点（slash_line の LINE_START と同じ）
const LINE_START = "2026-08-18";

const QUICK_RANGES = [
  { label: "全期間", days: null },
  { label: "7日", days: 7 },
  { label: "14日", days: 14 },
  { label: "30日", days: 30 },
  { label: "90日", days: 90 },
];

// LINE / WEB / 友だち の色。グラフ（SVGの属性）でも使うので JS に置き、
// ページの root に CSS 変数（--la-line 等）として流して scss からも参照する。
const COLORS = {
  line: "#3ddc97",
  web: "#9b7cff",
  friends: "#ff9f6b",
  block: "#ff6b6b",
  track: "rgba(255, 255, 255, 0.08)",
};

const ymd = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
};

const num = (v) => (v === null || v === undefined ? "—" : v.toLocaleString());
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const EVENT_STYLES = {
  follow: { label: "友だち追加", color: "#6c8cff" },
  block: { label: "ブロック", color: "#ff6b6b" },
  status: { label: "ステータス", color: "#8a8fae" },
  survey: { label: "アンケート", color: "#b07cff" },
  trial_booked: { label: "体験予約", color: "#4dd4ac" },
  trial_day: { label: "体験日", color: "#4dd4ac" },
  joined: { label: "入会", color: "#ffc857" },
  // LINE以外（既存WEB・店頭）で入会した人。連携して初めて見えるようになる
  joined_other: { label: "入会", color: "#c9a227" },
  account_link: { label: "連携", color: "#8a8fae" },
  sent: { label: "配信", color: "#6c8cff" },
  click: { label: "タップ", color: "#8aa4ff" },
  // 「これから送る予定」と「送らなかった」は別物。同じ警告色にすると、届く予定の
  // リマインドが失敗に見えて、実際に起きたことが埋もれる。
  scheduled: { label: "予定", color: "#4fb3d9" },
  not_sent: { label: "未送信", color: "#ff9f43" },
};

function Panel({ title, subtitle, children, actions }) {
  return (
    <Box className="la-panel">
      {(title || actions) && (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <Box>
            {title && <Typography className="la-panel-title">{title}</Typography>}
            {subtitle && <Typography className="la-muted la-small">{subtitle}</Typography>}
          </Box>
          {actions}
        </Stack>
      )}
      {children}
    </Box>
  );
}

// --- アイコン（線画・currentColor） ---

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconFriends = () => (
  <Icon>
    <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
    <circle cx="9" cy="7" r="3.5" />
    <path d="M22 19v-1a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);
const IconLink = () => (
  <Icon>
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
  </Icon>
);
const IconTrial = () => (
  <Icon>
    <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
    <path d="M16 2.5v4M8 2.5v4M3 10h18M9 15l2 2 4-4" />
  </Icon>
);
const IconJoin = () => (
  <Icon>
    <circle cx="12" cy="8" r="5" />
    <path d="M8.5 12.5 7 22l5-3 5 3-1.5-9.5" />
  </Icon>
);

// --- 1. ファネル（概要） ---

const share = (part, total) =>
  total ? Math.round((part * 1000) / total) / 10 : null;

function StatCard({ icon, tone, value, unit, label, sub }) {
  return (
    <Box className="la-stat">
      <Box className={`la-stat-icon la-stat-icon--${tone}`}>{icon}</Box>
      <Box minWidth={0}>
        <Typography className="la-stat-value">
          {num(value)}
          {unit && <span className="la-stat-unit">{unit}</span>}
        </Typography>
        <Typography className="la-stat-label">{label}</Typography>
        {sub && <Typography className="la-muted la-small">{sub}</Typography>}
      </Box>
    </Box>
  );
}

function Legend({ items }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="center" className="la-legend">
      {items.map((it) => (
        <span key={it.label}>
          <i style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </Stack>
  );
}

function ChartTooltip({ active, payload, label, unit = "人" }) {
  if (!active || !payload?.length) return null;
  return (
    <Box className="la-tooltip">
      <div className="la-tooltip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey}>
          <i style={{ background: p.color }} />
          {p.name} <strong>{num(p.value)}</strong>
          {unit}
        </div>
      ))}
    </Box>
  );
}

// 友だち追加のグラフ用。その日（週・月）の追加とブロック
function FriendsTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box className="la-tooltip">
      <div className="la-tooltip-title">{label}</div>
      <div>
        <i style={{ background: COLORS.friends }} />
        追加数 <strong>{num(p.friends_added)}</strong>人
      </div>
      <div>
        <i style={{ background: COLORS.block }} />
        ブロック <strong>{num(p.friends_blocked)}</strong>人
      </div>
    </Box>
  );
}

const TREND_METRICS = [
  { key: "trials", label: "体験申込" },
  { key: "joins", label: "入会" },
];

function TrendPanel({ series, granularity }) {
  const [metric, setMetric] = useState("trials");
  return (
    <Panel
      title="LINE と WEB の推移"
      subtitle={{ day: "日ごとの人数", week: "週ごとの人数" }[granularity] || "月ごとの人数"}
      actions={
        <Box className="la-segment">
          {TREND_METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              className={metric === m.key ? "is-active" : ""}
              onClick={() => setMetric(m.key)}
            >
              {m.label}
            </button>
          ))}
        </Box>
      }
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={series} barGap={4} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 4" />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
          <Bar dataKey={`${metric}_line`} name="LINE" fill={COLORS.line} barSize={9} radius={[5, 5, 0, 0]} />
          <Bar dataKey={`${metric}_web`} name="WEB" fill={COLORS.web} barSize={9} radius={[5, 5, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <Legend items={[{ label: "LINE", color: COLORS.line }, { label: "WEB", color: COLORS.web }]} />
    </Panel>
  );
}

function LinkRatePanel({ members: m }) {
  if (!m.available) {
    return (
      <Panel title="現会員のLINE連携">
        <Typography className="la-muted la-small">既存DB（manager_v2）を読めませんでした。</Typography>
      </Panel>
    );
  }
  const pie = [
    { name: "連携済み", value: m.linked, color: COLORS.line },
    { name: "未連携", value: m.active - m.linked, color: COLORS.track },
  ];
  return (
    <Panel title="現会員のLINE連携" subtitle="家族単位（同じメールアドレス＝1家族）・在籍中（休会を含む）・今日時点">
      <Box className="la-donut">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={pie} dataKey="value" innerRadius={66} outerRadius={80} startAngle={90}
              endAngle={-270} stroke="none" isAnimationActive={false}>
              {pie.map((p) => (
                <Cell key={p.name} fill={p.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <Box className="la-donut-center">
          <strong>{pct(m.rate)}</strong>
          <span>
            {num(m.linked)} / {num(m.active)}家族
          </span>
        </Box>
      </Box>
      <Legend items={pie.map((p) => ({ label: `${p.name} ${num(p.value)}`, color: p.color }))} />
    </Panel>
  );
}

function FriendsPanel({ friends: f, series }) {
  return (
    <Panel title="友だち追加とブロックの推移" subtitle="slash_line が受け取った記録（人数）">
      <ResponsiveContainer width="100%" height={210}>
        <BarChart data={series} barGap={4} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 4" />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
          <Tooltip content={<FriendsTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
          <Bar dataKey="friends_added" name="追加数" fill={COLORS.friends} barSize={9} radius={[5, 5, 0, 0]} />
          <Bar dataKey="friends_blocked" name="ブロック" fill={COLORS.block} barSize={9} radius={[5, 5, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <Legend items={[{ label: "追加数", color: COLORS.friends }, { label: "ブロック", color: COLORS.block }]} />
      <Typography className="la-muted la-small" mt={1}>
        期間内の友だち追加 {num(f.added)}人 ／ 期間内のブロック {num(f.blocked)}人。
        ブロックは slash_line とつながりのある人だけで、それ以前からの友だちのブロックは含みません。
      </Typography>
    </Panel>
  );
}

function CompareRow({ label, part }) {
  const linePct = share(part.line, part.total) ?? 0;
  return (
    <Box className="la-compare-row">
      <Box className="la-compare-head">
        <span>{label}</span>
        <span className="la-muted">合計 {num(part.total)}人</span>
      </Box>
      <Box className="la-compare-bar">
        <Box style={{ width: `${linePct}%`, background: COLORS.line }} />
        <Box style={{ width: `${100 - linePct}%`, background: COLORS.web }} />
      </Box>
      <Box className="la-compare-foot">
        <span>
          <i style={{ background: COLORS.line }} />
          LINE <strong>{num(part.line)}</strong>（{pct(share(part.line, part.total))}）
        </span>
        <span>
          <i style={{ background: COLORS.web }} />
          WEB <strong>{num(part.web)}</strong>
        </span>
      </Box>
    </Box>
  );
}

function ComparePanel({ trials, joins }) {
  return (
    <Panel title="LINE と WEB の比較" subtitle="期間内の人数（申込経路で分けています）">
      {trials.available ? <CompareRow label="体験申込" part={trials} /> : null}
      {joins.available ? <CompareRow label="入会" part={joins} /> : null}
      {!trials.available && !joins.available && (
        <Typography className="la-muted la-small">既存DB（manager_v2）を読めませんでした。</Typography>
      )}
    </Panel>
  );
}

function TrialBreakdownPanel({ trials }) {
  if (!trials.available) {
    return (
      <Panel title="体験の内訳">
        <Typography className="la-muted la-small">既存DB（manager_v2）を読めませんでした。</Typography>
      </Panel>
    );
  }
  const counted = trials.breakdown.filter((r) => !r.excluded);
  return (
    <Panel title="体験の内訳" subtitle="申込の時点で会員だったかどうか（人数）">
      <table className="la-table la-breakdown">
        <thead>
          <tr>
            <th>区分</th>
            <th className="la-num">LINE</th>
            <th className="la-num">WEB</th>
            <th className="la-num">計</th>
          </tr>
        </thead>
        <tbody>
          {counted.map((r) => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="la-num">{num(r.line)}</td>
              <td className="la-num">{num(r.web)}</td>
              <td className="la-num">{num(r.line + r.web)}</td>
            </tr>
          ))}
          {trials.breakdown
            .filter((r) => r.excluded)
            .map((r) => (
              <tr key={r.key} className="la-muted">
                <td>{r.label}</td>
                <td className="la-num">{num(r.line)}</td>
                <td className="la-num">{num(r.web)}</td>
                <td className="la-num">{num(r.line + r.web)}</td>
              </tr>
            ))}
        </tbody>
      </table>
      <Typography className="la-muted la-small" mt={1}>
        会員の家族＝在籍中の会員と同じメールで名前が違う人（兄弟姉妹など）。
        既存会員＝申込の時点で在籍中の会員本人（メール・名前が一致）で、体験の数には含めません。
      </Typography>
    </Panel>
  );
}

// 新規の友だちが LINE だけで 友だち追加 → 体験申込 → 入会 と進んだ人数
function LinePathPanel({ path }) {
  if (!path?.available) {
    return (
      <Panel title="LINEだけで進んだ新規の人">
        <Typography className="la-muted la-small">既存DB（manager_v2）を読めませんでした。</Typography>
      </Panel>
    );
  }
  const steps = [
    { label: "新規の友だち", value: path.friends, tone: "friends" },
    { label: "LINEで体験申込", value: path.trial, tone: "web", rate: path.rates.trial },
    { label: "LINEで入会", value: path.joined, tone: "line", rate: path.rates.join },
  ];
  return (
    <Panel
      title="LINEだけで進んだ新規の人"
      subtitle="期間内に友だち追加した新規の人が、LINEで体験申込 → LINEで入会と順に進んだ人数（体験・入会は今日まで）"
    >
      <Box className="la-path">
        {steps.map((st) => (
          <React.Fragment key={st.label}>
            {st.rate !== undefined && (
              <Box className="la-path-arrow">
                <strong>{pct(st.rate)}</strong>
                <span aria-hidden="true">→</span>
              </Box>
            )}
            <Box className={`la-path-step la-path-step--${st.tone}`}>
              <Typography className="la-path-value">
                {num(st.value)}
                <span className="la-stat-unit">人</span>
              </Typography>
              <Typography className="la-stat-label">{st.label}</Typography>
            </Box>
          </React.Fragment>
        ))}
      </Box>
      <Typography className="la-muted la-small" mt={1.5}>
        友だち追加から入会まで {pct(path.rates.overall)}。
        追加前から会員だった{num(path.existing)}人は除いています。体験は申込で数え、来場の有無は見ていません。
      </Typography>
    </Panel>
  );
}

// 入会した人を「どこで体験したか（行）× どこで入会したか（列）」で分けた表
function JoinByTrialPanel({ joins }) {
  if (!joins.available) {
    return (
      <Panel title="入会の内訳（体験の経路別）">
        <Typography className="la-muted la-small">既存DB（manager_v2）を読めませんでした。</Typography>
      </Panel>
    );
  }
  const rows = joins.by_trial;
  const sum = (key) => rows.reduce((acc, r) => acc + r[key], 0);
  return (
    <Panel title="入会の内訳（体験の経路別）" subtitle="期間内に入会した人を、どこで体験したかで分けています（人数）">
      <table className="la-table la-breakdown">
        <thead>
          <tr>
            <th>体験の経路</th>
            <th className="la-num">LINEで入会</th>
            <th className="la-num">WEBで入会</th>
            <th className="la-num">計</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="la-num">{num(r.line)}</td>
              <td className="la-num">{num(r.web)}</td>
              <td className="la-num">{num(r.line + r.web)}</td>
            </tr>
          ))}
          <tr className="la-breakdown-total">
            <td>計</td>
            <td className="la-num">{num(sum("line"))}</td>
            <td className="la-num">{num(sum("web"))}</td>
            <td className="la-num">{num(sum("line") + sum("web"))}</td>
          </tr>
        </tbody>
      </table>
      <Typography className="la-muted la-small" mt={1}>
        体験は入会より前の本人の申込を、期間を問わず探しています（LINE運用前の体験も含む）。
        兄弟姉妹の名前で体験して別の子が入会した場合は「体験の記録なし」になります。
      </Typography>
    </Panel>
  );
}

// 公開サイトの友だち追加ボタン（入口）ごとの押された回数
function EntryPanel({ entries, friendsAdded }) {
  const rows = entries || [];
  return (
    <Panel
      title="サイトの入口別"
      subtitle="公開サイトの友だち追加ボタンが押された回数（期間内）"
    >
      <table className="la-table la-breakdown">
        <thead>
          <tr>
            <th>入口</th>
            <th className="la-num">押された回数</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.entry}>
              <td>{r.label}</td>
              <td className="la-num">{num(r.clicks)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Typography className="la-muted la-small" mt={1}>
        同じ期間の友だち追加は {num(friendsAdded)}人。ボタンは公式LINEの友だち追加に飛ぶだけなので、
        押した人が実際に友だちになったかまでは結び付きません（同じ人が何度押しても回数に入ります）。
      </Typography>
    </Panel>
  );
}

function OverviewTab({ data }) {
  if (!data) return null;
  const { friends: f, members: m, trials: t, joins: j, series } = data;
  const ofTotal = (part, word) =>
    part.available ? `${word}全体 ${num(part.total)}人の ${pct(share(part.line, part.total))}` : "既存DBを読めませんでした";

  return (
    <Stack spacing={2.5}>
      <Box className="la-stat-row">
        <StatCard
          icon={<IconFriends />}
          tone="friends"
          value={f.added}
          label="期間内に追加された友だち"
          sub={`期間内のブロック ${num(f.blocked)}人`}
        />
        <StatCard
          icon={<IconTrial />}
          tone="web"
          value={t.line}
          label="LINEから体験申込"
          sub={ofTotal(t, "体験")}
        />
        <StatCard
          icon={<IconJoin />}
          tone="accent"
          value={j.line}
          label="LINEから入会"
          sub={ofTotal(j, "入会")}
        />
        <StatCard
          icon={<IconLink />}
          tone="line"
          value={m.linked}
          unit={m.available ? ` / ${num(m.active)}家族` : null}
          label="LINE連携済みの現会員（家族）"
          sub={m.available ? `連携率 ${pct(m.rate)}` : "既存DBを読めませんでした"}
        />
      </Box>

      <Box className="la-grid-main">
        <TrendPanel series={series} granularity={data.granularity} />
        <LinkRatePanel members={m} />
      </Box>

      <LinePathPanel path={data.line_path} />

      <EntryPanel entries={data.entries} friendsAdded={f.added} />

      <Box className="la-grid-bottom">
        <FriendsPanel friends={f} series={series} />
        <ComparePanel trials={t} joins={j} />
      </Box>

      <Box className="la-two-col">
        <TrialBreakdownPanel trials={t} />
        <JoinByTrialPanel joins={j} />
      </Box>

      <Typography className="la-muted la-small">
        体験・入会は申込経路で分けています（LINE＝LINEのフォームから申し込んだもの、WEB＝それ以外）。
        体験は在籍中の会員本人を除き、同じ人の複数申込を1人として数えます。友だち追加・ブロックは slash_line が受け取った記録で、公式LINEの友だち総数ではありません。
      </Typography>
    </Stack>
  );
}

// --- 2. シナリオ別 ---

// 割合（%・小数1桁、slash_line の _rate と同じ丸め）。分母が0なら 0%
const rateOf = (n, d) =>
  n === null || n === undefined ? null : d ? Math.round((n * 1000) / d) / 10 : 0;

const fmtMd = (iso) => (iso ? `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}` : "");

// 人数と、その下に割合。分母が無い（計測できない/判定しない）ときは理由を出す。
function PeopleCell({ value, rate, empty }) {
  if (value === null || value === undefined) {
    return <span className="la-muted la-small">{empty || "—"}</span>;
  }
  return (
    <>
      <div className="la-scn-n">{num(value)}人</div>
      {rate !== null && rate !== undefined && <div className="la-muted la-small">{pct(rate)}</div>}
    </>
  );
}

function TapCell({ s, sinceNote }) {
  if (s.tap_kind === "untracked") return <Chip size="small" label="計測なし" className="la-chip" />;
  if (s.tap_kind === "no_button") return <span className="la-muted la-small">ボタンなし</span>;
  return (
    <>
      <PeopleCell value={s.tapped} rate={s.tap_rate} />
      {s.tap_kind === "answer" && <div className="la-muted la-small">（アンケート回答）</div>}
      {s.tap_kind === "partial" && <div className="la-muted la-small">一部のボタンのみ</div>}
      {sinceNote && <div className="la-muted la-small">{sinceNote}</div>}
    </>
  );
}

// 送った人を100%として、タップ・目的の行動までの幅を1本の帯で見せる
function FunnelBar({ s }) {
  if (!s.recipients) return null;
  const w = (v) => `${Math.min(100, (v / s.recipients) * 100)}%`;
  return (
    <div className="la-scn-bars" aria-hidden>
      {s.tapped !== null && (
        <div className="la-scn-track">
          <span className="la-scn-bar--tap" style={{ width: w(s.tapped) }} />
        </div>
      )}
      {s.credited !== null && (
        <div className="la-scn-track">
          <span className="la-scn-bar--goal" style={{ width: w(s.credited) }} />
        </div>
      )}
    </div>
  );
}

// 期間の途中から計測を始めたボタンがあれば「※M/Dから計測」と添える
function tapSinceNote(s, from) {
  if (s.tapped === null) return "";
  const late = s.buttons.filter((b) => b.since && from < b.since);
  if (!late.length) return "";
  const since = fmtMd(late.map((b) => b.since).sort().pop());
  return late.length === s.buttons.length ? `※${since}から計測` : `※一部のボタンは${since}から計測`;
}

// アンケートの回答の内訳（最後の回答で1人1回）
function SurveyAnswers({ s }) {
  return (
    <>
      <Typography className="la-small la-muted">
        アンケートの回答（{num(s.answered)}人・押し直した人は最後の回答で数えます）
      </Typography>
      <table className="la-table">
        <tbody>
          {s.answers.map((a) => (
            <tr key={a.code}>
              <td>{a.label}</td>
              <td className="la-num">
                <span className="la-scn-n">{num(a.count)}人</span>{" "}
                <span className="la-muted la-small">{pct(a.rate)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ScenarioDetail({ s, goalLabel, days }) {
  return (
    <Box className="la-two-col">
      <Box>
        {!s.answers && (
          <Typography className="la-small la-muted">ボタンごとのタップ（押した人数）</Typography>
        )}
        {s.buttons.length ? (
          <table className="la-table">
            <tbody>
              {s.buttons.map((b, i) => (
                <tr key={i}>
                  <td>{b.label}</td>
                  <td className="la-num">
                    {b.tracked ? (
                      <>
                        <span className="la-scn-n">{num(b.tapped ?? 0)}人</span>
                        {b.since && (
                          <div className="la-muted la-small">{fmtMd(b.since)}から計測</div>
                        )}
                      </>
                    ) : (
                      <span className="la-muted la-small">計測できません</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : s.answers ? (
          <SurveyAnswers s={s} />
        ) : (
          <Typography className="la-muted la-small">ボタンなし</Typography>
        )}
        <Typography className="la-small la-muted" sx={{ mt: 1 }}>
          送った通数 {num(s.messages)}通（同じ人に複数回送ることがあります）
        </Typography>
      </Box>
      <Box>
        {s.reached_any !== null && (
          <Box sx={{ mb: 1.5 }}>
            <Typography className="la-small la-muted">参考：受け取った人のうち{days}日以内に{goalLabel}人</Typography>
            <Typography>
              <span className="la-scn-n">{num(s.reached_any)}人</span>{" "}
              <span className="la-muted la-small">
                （{pct(s.reached_any_rate)}・ほかのシナリオと重複あり）
              </span>
            </Typography>
          </Box>
        )}
        <Typography className="la-small la-muted">届かなかった理由（予定したが送らなかった）</Typography>
        {s.skip_reasons.length ? (
          <table className="la-table">
            <tbody>
              {s.skip_reasons.map((r) => (
                <tr key={r.code}>
                  <td>{r.label}</td>
                  <td className="la-num">{num(r.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Typography className="la-muted la-small">なし</Typography>
        )}
      </Box>
    </Box>
  );
}

function ScenarioGroup({ group, rows, data, openId, setOpenId }) {
  const hasGoal = group.key !== "none";
  const days = data.window_days;
  const cols = hasGoal ? 5 : 3;
  const total = group.total;

  return (
    <Panel
      title={group.label}
      subtitle={
        hasGoal
          ? `「${group.goal_label}」は1人1回だけ、その直前（${days}日以内）に最後にタップしたシナリオ、タップが無ければ最後に届いたシナリオに数えています。行をクリックするとボタンごとのタップが開きます`
          : "体験・入会を目的としないシナリオです（成果は判定しません）。行をクリックするとボタンごとのタップが開きます"
      }
    >
      <Box className="la-scroll">
        <table className="la-table la-table--grid la-scn-table">
          <thead>
            <tr>
              <th>シナリオ</th>
              <th className="la-num">送った人</th>
              <th className="la-num">ボタンをタップ</th>
              {hasGoal && <th className="la-num">{group.goal_label}（1人1回）</th>}
              {hasGoal && <th className="la-num">うちタップから</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <React.Fragment key={s.scenario_id}>
                <tr
                  className="la-row-click"
                  onClick={() => setOpenId(openId === s.scenario_id ? null : s.scenario_id)}
                >
                  <td>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <strong>{s.scenario_id}</strong>
                      <span>{s.name}</span>
                      {s.enabled === false && (
                        <Chip size="small" label="配信OFF" className="la-chip la-chip--off" />
                      )}
                    </Stack>
                    {hasGoal && <FunnelBar s={s} />}
                  </td>
                  <td className="la-num">
                    <div className="la-scn-n">{num(s.recipients)}人</div>
                  </td>
                  <td className="la-num">
                    <TapCell s={s} sinceNote={tapSinceNote(s, data.period.from)} />
                  </td>
                  {hasGoal && (
                    <td className="la-num">
                      <PeopleCell value={s.credited} rate={s.credited_rate} />
                    </td>
                  )}
                  {hasGoal && (
                    <td className="la-num">
                      <PeopleCell
                        value={s.tapped === null ? null : s.credited_via_tap}
                        rate={s.tapped === null ? null : rateOf(s.credited_via_tap, s.credited)}
                        empty={s.tap_kind === "untracked" ? "タップを計測できません" : "—"}
                      />
                    </td>
                  )}
                </tr>
                {openId === s.scenario_id && (
                  <tr className="la-detail-row">
                    <td colSpan={cols}>
                      <ScenarioDetail s={s} goalLabel={group.goal_label} days={days} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          {total && (
            <tfoot>
              <tr className="la-scn-total">
                <td>
                  <strong>グループ合計</strong>
                  <div className="la-muted la-small">同じ人は1人として数えています</div>
                </td>
                <td className="la-num">
                  <div className="la-scn-n">{num(total.recipients)}人</div>
                </td>
                <td className="la-num">
                  <PeopleCell value={total.tapped} rate={total.tap_rate} />
                </td>
                {hasGoal && (
                  <td className="la-num">
                    <PeopleCell value={total.credited} rate={total.credited_rate} />
                  </td>
                )}
                {hasGoal && (
                  <td className="la-num">
                    <PeopleCell
                      value={total.credited_via_tap}
                      rate={rateOf(total.credited_via_tap, total.credited)}
                    />
                  </td>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </Box>
    </Panel>
  );
}

function ScenarioTab({ data }) {
  const [openId, setOpenId] = useState(null);
  if (!data) return null;

  const groups = data.groups
    .map((g) => ({ group: g, rows: data.scenarios.filter((s) => s.group === g.key) }))
    .filter((g) => g.rows.length);

  // 期間が、途中から計測を始めたボタンの開始日より前を含むか（含むならタップが少なく出る）
  const lateSince = data.scenarios
    .flatMap((s) => s.buttons.map((b) => b.since))
    .filter((d) => d && data.period.from < d)
    .sort()
    .pop();

  return (
    <Stack spacing={2}>
      {lateSince && (
        <Alert severity="warning" className="la-alert la-alert--warn">
          選んだ期間に、予約・入会フォームと会員アプリへのボタンを計測する前（{fmtMd(lateSince)}より前）が含まれています。
          そのぶん「ボタンをタップ」「うちタップから」は少なく出ます（送った人数や入会・来場・予約の合計人数には影響しません）。
          タップを比べるときは {fmtMd(lateSince)} 以降の期間を選んでください。
        </Alert>
      )}
      <Alert severity="info" className="la-alert">
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {data.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </Box>
      </Alert>

      <Stack direction="row" spacing={2} className="la-scn-legend la-small la-muted">
        <span>
          <i className="la-scn-dot la-scn-dot--tap" />
          タップした人
        </span>
        <span>
          <i className="la-scn-dot la-scn-dot--goal" />
          目的の行動をした人（送った人を100%とした割合）
        </span>
      </Stack>

      {groups.map(({ group, rows }) => (
        <ScenarioGroup
          key={group.key}
          group={group}
          rows={rows}
          data={data}
          openId={openId}
          setOpenId={setOpenId}
        />
      ))}
      {!groups.length && (
        <Panel>
          <Typography className="la-muted">この期間の配信はありません。</Typography>
        </Panel>
      )}
    </Stack>
  );
}

// --- 3. 人の動き ---

// --- LINE連携（現会員の一覧） ---

const fmtDate = (iso) => (iso ? iso.slice(0, 10).replaceAll("-", "/") : "—");

// 現会員を家族（同じメール）ごとに1行にまとめ、連携済み・未連携を見る
function LinkMembersTab({ tenant }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    lineConsole
      .analyticsLinkMembers(tenant)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [tenant]);

  const rows = useMemo(() => {
    if (!data?.families) return [];
    const q = query.replace(/\s/g, "");
    return data.families.filter(
      (f) =>
        (!state || f.state === state) &&
        (!q ||
          f.members.some(
            (m) =>
              m.name.replace(/\s/g, "").includes(q) ||
              m.classes.some((c) => c.replace(/\s/g, "").includes(q))
          ) ||
          f.line_names.some((n) => n.replace(/\s/g, "").includes(q)))
    );
  }, [data, state, query]);

  if (error) {
    return (
      <Alert severity="error" className="la-alert">
        取得できませんでした: {error}
      </Alert>
    );
  }
  if (!data) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!data.available) {
    return (
      <Alert severity="warning" className="la-alert">
        既存DB（manager_v2）を読めないため、会員の一覧を表示できません。
      </Alert>
    );
  }

  const labels = Object.fromEntries(data.states.map((st) => [st.value, st.label]));
  return (
    <Panel
      title="現会員のLINE連携"
      subtitle="在籍中（休会を含む）の会員を家族（同じメールアドレス）ごとにまとめ、最近入会した家族から並べています"
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={1.5}>
        <Box className="la-segment">
          <button type="button" className={state === "" ? "is-active" : ""} onClick={() => setState("")}>
            すべて {num(data.total)}
          </button>
          {data.states.map((st) => (
            <button
              key={st.value}
              type="button"
              className={state === st.value ? "is-active" : ""}
              onClick={() => setState(st.value)}
            >
              {st.label} {num(st.count)}
            </button>
          ))}
        </Box>
        <TextField
          size="small"
          placeholder="生徒名・クラス・LINEの名前で検索"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Typography className="la-muted la-small">{num(rows.length)}家族</Typography>
      </Stack>
      <Box className="la-scroll la-link-scroll">
        <table className="la-table la-breakdown la-link-table">
          <thead>
            <tr>
              <th>生徒（家族）</th>
              <th>受講クラス</th>
              <th>入会日</th>
              <th>状態</th>
              <th>LINEの名前</th>
              <th className="la-num">連携日</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) =>
              f.members.map((m, i) => (
                <tr key={m.id} className={i === 0 ? "la-family-first" : "la-family-next"}>
                  <td>
                    {m.name || "（名前なし）"}
                    {m.flags.map((flag) => (
                      <span key={flag} className="la-member-flag">
                        {flag}
                      </span>
                    ))}
                  </td>
                  <td>
                    {m.classes.length
                      ? m.classes.map((c) => <div key={c}>{c}</div>)
                      : "—"}
                    {m.classes_from && (
                      <div className="la-muted la-small">{Number(m.classes_from.slice(5))}月から</div>
                    )}
                  </td>
                  <td>{fmtDate(m.joined_at)}</td>
                  {i === 0 && (
                    <>
                      <td rowSpan={f.members.length}>
                        <span className={`la-link-state la-link-state--${f.state}`}>{labels[f.state]}</span>
                      </td>
                      <td rowSpan={f.members.length} className="la-break">
                        {f.line_names.length ? f.line_names.map((n) => <div key={n}>{n}</div>) : "—"}
                      </td>
                      <td rowSpan={f.members.length} className="la-num">
                        {fmtDate(f.linked_at)}
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Box>
      <Typography className="la-muted la-small" mt={1}>
        家族の誰か1人でも連携していれば連携済みです。ブロック中＝連携したLINEがすべてブロック中で、お知らせが届かない家族です。
        LINEの名前は、連携した時点のLINEの表示名です。受講クラスは今月のプランで、来月から始まる人はそのプランを出しています。
      </Typography>
    </Panel>
  );
}

function UsersTab({ tenant }) {
  const [query, setQuery] = useState("");
  const [statusTag, setStatusTag] = useState("");
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await lineConsole.analyticsUsers({ q: query, status_tag: statusTag }, tenant));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [query, statusTag, tenant]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTag]);

  const openUser = async (u) => {
    setSelected(u);
    setTimeline(null);
    try {
      setTimeline(await lineConsole.analyticsUserTimeline(u.id, tenant));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box className="la-users">
      <Panel title="LINEユーザー" subtitle="表示名・userId・生徒名で検索できます">
        <Stack direction="row" spacing={1} mb={1.5}>
          <TextField
            size="small"
            fullWidth
            placeholder="名前 / userId / 生徒名"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
          <Button variant="contained" onClick={load}>
            検索
          </Button>
        </Stack>
        <TextField
          select
          size="small"
          fullWidth
          label="ステータス"
          value={statusTag}
          onChange={(e) => setStatusTag(e.target.value)}
          SelectProps={{ MenuProps: MENU_PROPS }}
        >
          <MenuItem value="">すべて</MenuItem>
          {(list?.status_options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        {error && <Alert severity="error" className="la-alert">{error}</Alert>}
        {loading && <CircularProgress size={20} sx={{ mt: 2 }} />}

        <Box className="la-user-list">
          {(list?.users || []).map((u) => (
            <Box
              key={u.id}
              className={`la-user-item ${selected?.id === u.id ? "is-active" : ""}`}
              onClick={() => openUser(u)}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <strong>{u.display_name || u.line_user_id}</strong>
                <Chip size="small" label={u.status_label} className="la-chip" />
              </Stack>
              <Typography className="la-muted la-small">
                配信 {num(u.sent)} 通
                {u.members.length ? ` / 生徒 ${u.members.map((m) => m.name).join("・")}` : ""}
                {u.opt_out ? " / 配信停止中" : ""}
                {u.auto_scenario_paused ? " / 個別対応中" : ""}
              </Typography>
            </Box>
          ))}
          {list && !list.users.length && (
            <Typography className="la-muted la-small">該当するユーザーがいません。</Typography>
          )}
        </Box>
      </Panel>

      <Panel
        title={selected ? selected.display_name || selected.line_user_id : "時系列"}
        subtitle={selected ? "配信・タップ・ステータス・予約・入会を1本に並べています" : "左のユーザーを選んでください"}
      >
        {selected && !timeline && <CircularProgress size={20} />}
        {timeline && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" mb={1.5}>
              <Chip size="small" className="la-chip" label={timeline.user.status_label} />
              {timeline.user.members.map((m) => (
                <Chip
                  key={m.member_ref}
                  size="small"
                  className="la-chip"
                  label={`${m.name || m.member_ref}（${m.origin_label}）`}
                />
              ))}
            </Stack>
            <Box className="la-timeline">
              {timeline.events.map((e, i) => {
                const style = EVENT_STYLES[e.kind] || { label: e.kind, color: "#8a8fae" };
                return (
                  <Box key={i} className="la-event">
                    <Box className="la-event-dot" style={{ background: style.color }} />
                    <Box className="la-event-body">
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography className="la-small la-muted">
                          {new Date(e.at).toLocaleString("ja-JP")}
                        </Typography>
                        <Chip
                          size="small"
                          className="la-chip"
                          label={style.label}
                          style={{ borderColor: style.color }}
                        />
                      </Stack>
                      <Typography>{e.title}</Typography>
                      {e.detail && (
                        <Typography className="la-muted la-small la-break">{e.detail}</Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
              {!timeline.events.length && (
                <Typography className="la-muted la-small">記録がありません。</Typography>
              )}
            </Box>
          </>
        )}
      </Panel>
    </Box>
  );
}

// --- ページ本体 ---

function LineAnalyticsPage() {
  useLoginCheck();

  const tenant = "slash";
  const [tab, setTab] = useState("funnel");
  const [from, setFrom] = useState(LINE_START);
  const [to, setTo] = useState(ymd(new Date()));
  const [overview, setOverview] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (period) => {
      setLoading(true);
      setError(null);
      try {
        const [o, s] = await Promise.all([
          lineConsole.analyticsOverview(period, tenant),
          lineConsole.analyticsScenarios(period, tenant),
        ]);
        setOverview(o);
        setScenarios(s);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [tenant]
  );

  useEffect(() => {
    load({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 期間ボタンは押したらすぐ集計し直す（日付を手で変えたときだけ「適用する」）
  const applyQuick = (days) => {
    const period = { from: days ? daysAgo(days) : LINE_START, to: ymd(new Date()) };
    setFrom(period.from);
    setTo(period.to);
    load(period);
  };

  return (
    <Box
      className="line-analytics-page"
      style={{
        "--la-line": COLORS.line,
        "--la-web": COLORS.web,
        "--la-friends": COLORS.friends,
        "--la-block": COLORS.block,
        "--la-track": COLORS.track,
      }}
    >
      <Box className="la-header">
        <Box>
          <Typography variant="h5" className="la-title">
            LINE配信の分析
          </Typography>
          <Typography className="la-muted la-small">
            公式LINEの友だち・会員の連携状況と、LINE経由の体験・入会をWEBと比べて見る画面です。
          </Typography>
          <Typography className="la-muted la-small">
            集計はLINE配信の運用を始めた {LINE_START.replaceAll("-", "/")} 以降のデータです（それより前の日付を指定しても、この日から集計します）。
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {QUICK_RANGES.map((r) => (
            <Button key={r.label} size="small" variant="outlined" onClick={() => applyQuick(r.days)}>
              {r.label}
            </Button>
          ))}
          <TextField
            size="small"
            type="date"
            label="開始"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="終了"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <Button variant="contained" onClick={() => load({ from, to })} disabled={loading}>
            {loading ? "集計中…" : "適用する"}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" className="la-alert">
          集計を取得できませんでした: {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className="la-tabs"
        textColor="inherit"
        TabIndicatorProps={{ className: "la-tab-indicator" }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>

      {loading && !overview ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : (
        <Box className="la-body">
          {tab === "funnel" && <OverviewTab data={overview} />}
          {tab === "scenarios" && <ScenarioTab data={scenarios} />}
          {tab === "link" && <LinkMembersTab tenant={tenant} />}
          {tab === "users" && <UsersTab tenant={tenant} />}
        </Box>
      )}
    </Box>
  );
}

export default LineAnalyticsPage;
